import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, AED_short, AED } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Bell, Heart, ListChecks, Building2, Sparkles, Gavel, Clock,
  MapPin, ArrowRight, Loader2, ChevronRight, Radar, TrendingUp, Zap
} from 'lucide-react';

function timeLeft(iso) {
  if (!iso) return { text: '—', urgent: false, ended: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { text: 'Ended', urgent: false, ended: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d >= 1) return { text: `${d}d ${h}h`, urgent: d < 2, ended: false };
  const m = Math.floor((ms % 3600000) / 60000);
  return { text: `${h}h ${m}m`, urgent: true, ended: false };
}

const PRIORITY_TINT = {
  urgent: 'bg-red-500/10 text-red-700 border-red-300',
  high:   'bg-amber-500/10 text-amber-700 border-amber-300',
  medium: 'bg-blue-500/10 text-blue-700 border-blue-300',
  low:    'bg-slate-500/10 text-slate-600 border-slate-300',
};

export default function MyHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [radar, setRadar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/me/dashboard').then(r => setData(r.data)),
      api.get('/me/deal-radar').then(r => setRadar(r.data)).catch(() => setRadar({ top_deals: [] })),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 max-w-[1400px] mx-auto flex items-center gap-2 text-slate-500">
        <Loader2 size={16} className="animate-spin"/> Loading your workspace…
      </div>
    );
  }

  const d = data || {};
  const watchCount = (d.watchlist || []).length;
  const taskCount = (d.my_tasks || []).length;
  const propCount = (d.my_properties || []).length;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="me-page">
      <div className="flex items-center gap-4 mb-6">
        <img
          src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=1A2530&color=fff`}
          alt="" className="w-14 h-14 rounded-full border-2 border-white shadow-md"
        />
        <div>
          <h1 className="font-serif text-3xl">Welcome back, {user?.name?.split(' ')[0] || 'there'}</h1>
          <p className="text-sm text-slate-500">{user?.role?.replace('_', ' ')} · Iris Realty</p>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid md:grid-cols-4 gap-3 mb-6" data-testid="me-stats">
        <StatCard icon={ListChecks} label="Open tasks" value={taskCount} accent="text-blue-600" to="/app/tasks"/>
        <StatCard icon={Heart} label="Watching" value={watchCount} accent="text-red-500" to="/app/auctions?filter=watched"/>
        <StatCard icon={Building2} label="Properties" value={propCount} accent="text-[#679286]" to="/app/properties"/>
        <StatCard icon={Bell} label="Unread" value={d.unread_notifications || 0} accent="text-amber-600"/>
      </div>

      {/* AI Deal Radar */}
      <DealRadar radar={radar} onRefresh={async () => {
        try { const r = await api.get('/me/deal-radar'); setRadar(r.data); } catch { /* ignore */ }
      }}/>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Watchlist */}
        <div className="glass p-5" data-testid="me-watchlist">
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={16} className="text-slate-600"/>
            <h2 className="font-serif text-xl">Auction watchlist</h2>
            <Link to="/app/auctions?filter=watched" className="ml-auto text-xs text-[#679286] font-semibold flex items-center gap-1">
              Open all <ArrowRight size={12}/>
            </Link>
          </div>
          {watchCount === 0 ? (
            <div className="text-center py-10">
              <Heart size={24} className="mx-auto mb-2 text-slate-300"/>
              <div className="text-sm text-slate-500">You haven&apos;t watched any auctions yet</div>
              <Link to="/app/auctions" className="inline-block mt-3 text-xs font-semibold text-[#679286]">Browse auctions →</Link>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(d.watchlist || []).slice(0, 6).map(a => {
                const tl = timeLeft(a.auction_end_at);
                return (
                  <Link
                    key={a.external_id} to={`/app/auctions?open=${a.external_id}`}
                    data-testid={`me-watch-${a.external_id}`}
                    className="flex gap-3 p-3 rounded-xl bg-white/70 border border-white/60 hover:bg-white transition"
                  >
                    {(a.images || [])[0] && (
                      <img src={a.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover"/>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin size={10}/> {a.area || a.emirate}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className="font-semibold text-[#679286]">{AED_short(a.current_bid_aed)}</span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                          tl.ended ? 'bg-slate-200 text-slate-500' : tl.urgent ? 'bg-red-500 text-white' : 'bg-slate-800 text-white'
                        }`}>
                          <Clock size={9}/> {tl.text}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* My tasks */}
        <div className="glass p-5" data-testid="me-tasks">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks size={16} className="text-slate-600"/>
            <h2 className="font-serif text-xl">Your open tasks</h2>
            <Link to="/app/tasks" className="ml-auto text-xs text-[#679286] font-semibold flex items-center gap-1">
              Open all <ArrowRight size={12}/>
            </Link>
          </div>
          {taskCount === 0 ? (
            <div className="text-center py-10">
              <ListChecks size={24} className="mx-auto mb-2 text-slate-300"/>
              <div className="text-sm text-slate-500">No open tasks assigned to you.</div>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(d.my_tasks || []).slice(0, 8).map(t => (
                <Link
                  key={t.id} to={`/app/tasks?open=${t.id}`}
                  data-testid={`me-task-${t.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-white/60 hover:bg-white transition"
                >
                  <div className={`w-1 self-stretch rounded-full ${
                    t.priority === 'urgent' ? 'bg-red-500' :
                    t.priority === 'high' ? 'bg-amber-500' :
                    t.priority === 'medium' ? 'bg-blue-400' : 'bg-slate-300'
                  }`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t.assigned_by_name ? `From ${t.assigned_by_name}` : '—'}{t.due_date ? ` · Due ${t.due_date}` : ''}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${PRIORITY_TINT[t.priority] || PRIORITY_TINT.medium}`}>
                    {t.priority || 'medium'}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent AI analyses */}
        <div className="glass p-5" data-testid="me-ai-history">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-slate-600"/>
            <h2 className="font-serif text-xl">Your recent AI insights</h2>
          </div>
          {(!d.recent_ai_analyses || d.recent_ai_analyses.length === 0) ? (
            <div className="text-center py-10">
              <Sparkles size={24} className="mx-auto mb-2 text-slate-300"/>
              <div className="text-sm text-slate-500">Run Iris AI on any auction or deal to see history here.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {(d.recent_ai_analyses || []).slice(0, 5).map(x => (
                <Link
                  key={x.auction_id}
                  to={`/app/auctions?open=${x.auction_id}`}
                  data-testid={`me-ai-${x.auction_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-white/60 hover:bg-white transition"
                >
                  <div className="w-10 h-10 rounded-full bg-[#679286]/10 grid place-items-center">
                    <Sparkles size={14} className="text-[#679286]"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">Auction #{x.auction_id}</div>
                    <div className="text-xs text-slate-500 truncate">{x.analysis?.verdict || x.analysis?.summary}</div>
                  </div>
                  {x.analysis?.deal_score != null && (
                    <div className="text-lg font-serif text-[#679286]">{Math.round(x.analysis.deal_score)}</div>
                  )}
                  <ChevronRight size={14} className="text-slate-400"/>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Your properties */}
        <div className="glass p-5" data-testid="me-properties">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={16} className="text-slate-600"/>
            <h2 className="font-serif text-xl">Your properties</h2>
            <Link to="/app/properties" className="ml-auto text-xs text-[#679286] font-semibold flex items-center gap-1">
              Open all <ArrowRight size={12}/>
            </Link>
          </div>
          {propCount === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No properties assigned.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
              {(d.my_properties || []).slice(0, 8).map(p => (
                <Link
                  key={p.id} to={`/app/properties/${p.id}`}
                  data-testid={`me-prop-${p.id}`}
                  className="p-3 rounded-xl bg-white/70 border border-white/60 hover:bg-white transition"
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{p.stage?.replace('_', ' ')}</div>
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={10}/> {p.community}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, to }) {
  const inner = (
    <motion.div
      whileHover={{ y: -2 }}
      className="glass p-4 flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-full bg-white/70 grid place-items-center">
        <Icon size={16} className={accent}/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`font-serif text-2xl ${accent}`}>{value}</div>
      </div>
    </motion.div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

// ---------- AI Deal Radar ----------
function DealRadar({ radar, onRefresh }) {
  if (!radar) return null;
  const deals = radar.top_deals || [];
  const hasDeals = deals.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="relative rounded-3xl overflow-hidden mb-6 border border-white/70 shadow-lg"
      data-testid="me-deal-radar"
    >
      {/* Ambient gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A2530] via-[#243544] to-[#0F1720]"/>
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#679286]/30 blur-3xl"/>
      <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl"/>

      <div className="relative p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-white/10 grid place-items-center backdrop-blur-sm">
            <Radar size={18} className="text-[#9BC7B8]"/>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-serif text-2xl text-white">Iris AI Deal Radar</h2>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-[#9BC7B8]">
                <Zap size={9} className="inline mr-0.5"/> Live
              </span>
            </div>
            <p className="text-xs text-slate-300">
              {radar.preferences_applied ? 'Top 5 deals matched to your preferences · listings + auctions' : 'Top 5 unified deals — listings + auctions'}
            </p>
          </div>
          {hasDeals && (
            <Link to="/app/trends" className="text-xs text-[#9BC7B8] hover:text-white flex items-center gap-1 font-medium">
              See all <ArrowRight size={12}/>
            </Link>
          )}
        </div>

        {!hasDeals ? (
          <div className="mt-6 py-8 text-center">
            <div className="text-sm text-slate-300">
              {radar.message || 'No deals surfaced yet. Open Trends and let Iris scan for opportunities.'}
            </div>
            <Link to="/app/trends" className="inline-flex items-center gap-2 mt-3 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold">
              <Radar size={12}/> Open Deal Radar
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {deals.map((deal, i) => <RadarCard key={deal.source_id || deal.auction_id || i} deal={deal} rank={i + 1}/>)}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function AnalyzeAuctionsButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    toast.loading('Iris AI is analyzing top auctions…', { id: 'analyze-radar', duration: 60000 });
    try {
      const r = await api.post('/me/deal-radar/analyze-batch');
      toast.dismiss('analyze-radar');
      const analyzed = r.data?.analyzed || 0;
      const attempted = r.data?.attempted || 0;
      if (analyzed > 0) {
        toast.success(`Iris AI scored ${analyzed} new auction${analyzed === 1 ? '' : 's'}. Refreshing radar…`);
      } else if (attempted === 0) {
        toast.info(r.data?.message || 'No live auctions found in this workspace. Sync the auction list first from /app/auctions.', { duration: 8000 });
      } else {
        toast.error(`Attempted ${attempted} but AI analysis failed on all of them — check the EMERGENT_LLM_KEY and Claude quota.`, { duration: 10000 });
      }
      if (onDone) await onDone();
    } catch (e) {
      toast.dismiss('analyze-radar');
      const msg = e?.response?.data?.detail || e?.message || 'Analysis failed';
      toast.error(String(msg), { duration: 10000 });
    } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy}
      data-testid="me-analyze-auctions-btn"
      className="inline-flex items-center gap-2 mt-3 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold disabled:opacity-60">
      {busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
      {busy ? 'Iris AI is analyzing… (up to 30s)' : 'Analyze top 5 auctions with Iris AI'}
    </button>
  );
}

function RadarCard({ deal, rank }) {
  // Support BOTH the legacy auction shape (deal.listing + deal.recommendation)
  // AND the new unified shape (deal.source + deal.cover_photo_url + deal.hotness_score).
  const isUnified = deal.source !== undefined;
  const listing = deal.listing || {};
  const isAuction = isUnified ? deal.source === 'auction' : true;
  const score = isUnified ? (deal.hotness_score || 0) : (deal.score || 0);
  const img = isUnified
    ? deal.cover_photo_url
    : (listing.images || [])[0];
  const title = isUnified
    ? (deal.building || deal.community || (isAuction ? 'Auction lot' : 'Listing'))
    : (listing.title || 'Auction listing');
  const community = isUnified
    ? deal.community
    : (listing.address || listing.area || listing.emirate);
  const currentBid = isUnified
    ? (deal.current_bid_aed || deal.price_aed)
    : listing.current_bid_aed;
  const verdict = deal.verdict;
  const recommendation = isUnified
    ? (score >= 85 ? 'bid_aggressive' : score >= 70 ? 'bid_conservative' : score >= 50 ? 'watch' : 'skip')
    : deal.recommendation;
  const isWatched = deal.is_watched;
  const auctionEnd = isUnified ? deal.auction_end_at : listing.auction_end_at;
  const href = isUnified
    ? (isAuction ? `/app/auctions?open=${deal.source_id}` : deal.listing_url)
    : `/app/auctions?open=${deal.auction_id}`;
  const external = isUnified && !isAuction;
  const matchesPrefs = deal.matches_prefs;
  const scoreColor = score >= 75 ? 'text-emerald-300' : score >= 50 ? 'text-amber-300' : 'text-red-300';
  const recTint = {
    bid_aggressive: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
    bid_conservative: 'bg-blue-500/20 text-blue-200 border-blue-400/30',
    watch: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
    skip: 'bg-red-500/20 text-red-200 border-red-400/30',
  }[recommendation] || 'bg-white/10 text-slate-200 border-white/20';

  const tl = (() => {
    if (!auctionEnd) return isAuction ? '—' : 'Live';
    const ms = new Date(auctionEnd).getTime() - Date.now();
    if (ms <= 0) return 'Ended';
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    return d >= 1 ? `${d}d ${h}h` : `${h}h`;
  })();

  const cardContent = (
    <>
      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1.5">
        <div className="w-7 h-7 rounded-full bg-white/90 text-slate-900 grid place-items-center font-serif text-sm shadow-lg">{rank}</div>
        <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold shadow ${
          isAuction ? 'bg-purple-600 text-white' : 'bg-emerald-600 text-white'
        }`}>{isAuction ? 'AUCT' : 'LIST'}</span>
      </div>
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 items-end">
        {isWatched && (
          <div className="w-7 h-7 rounded-full bg-red-500/90 grid place-items-center shadow"><Heart size={12} className="fill-white text-white"/></div>
        )}
        {matchesPrefs === true && (
          <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#679286] text-white font-bold shadow"><Sparkles size={8} className="inline mr-0.5"/>Match</span>
        )}
        {matchesPrefs === false && (
          <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/20 text-white/80 backdrop-blur-sm">Outside</span>
        )}
      </div>
      <div className="h-32 bg-slate-800 relative overflow-hidden">
        {img && <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
        <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between">
          <div className={`font-serif text-3xl leading-none ${scoreColor} drop-shadow-lg`}>{Math.round(score || 0)}<span className="text-sm opacity-70">/100</span></div>
          <div className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${recTint}`}>
            {(recommendation || 'watch').replace('_', ' ')}
          </div>
        </div>
      </div>
      <div className="p-3">
        <div className="text-[10px] text-slate-400 flex items-center gap-1 mb-1">
          <MapPin size={10}/> {community || '—'}
        </div>
        <div className="text-sm font-medium text-white line-clamp-1 mb-2">{title}</div>
        {verdict && <div className="text-[11px] text-slate-300 line-clamp-2 mb-3 min-h-[2rem]">{verdict}</div>}
        <div className="grid grid-cols-3 gap-1 text-[10px]">
          <div>
            <div className="text-slate-500 uppercase tracking-widest">{isAuction ? 'Time' : 'Type'}</div>
            <div className="text-white font-medium">{isAuction ? tl : (deal.property_type || '—')}</div>
          </div>
          <div>
            <div className="text-slate-500 uppercase tracking-widest">{isAuction ? 'Bid' : 'Asking'}</div>
            <div className="text-[#9BC7B8] font-medium">{AED_short(currentBid)}</div>
          </div>
          <div>
            <div className="text-slate-500 uppercase tracking-widest">psf</div>
            <div className="text-emerald-300 font-medium">{deal.aed_per_sqft ? Math.round(deal.aed_per_sqft).toLocaleString() : '—'}</div>
          </div>
        </div>
      </div>
    </>
  );

  const testId = `radar-deal-${deal.source_id || deal.auction_id}`;
  const cls = "group relative rounded-2xl overflow-hidden bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-sm transition-all block";
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>{cardContent}</a>
  ) : (
    <Link to={href || '#'} data-testid={testId} className={cls}>{cardContent}</Link>
  );
}
